/**
 * Company-wide prompt template management for company admins.
 * Lists only company-scoped templates; create/edit/delete sets isCompanyTemplate=true.
 */

import React, { useState, useEffect } from 'react';
import {
  promptTemplateService,
  PromptTemplate,
} from '../../services/promptTemplateService';

const CATEGORIES = ['general', 'analysis', 'report', 'legal', 'underwriting', 'workflow'];

interface FormData {
  title: string;
  prompt: string;
  description: string;
  category: string;
}

const empty = (): FormData => ({ title: '', prompt: '', description: '', category: 'general' });

const CompanyPromptTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PromptTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<FormData>(empty());

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await promptTemplateService.getUserTemplates({
        includeGlobal: false,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      });
      setTemplates(res.templates.filter((t) => t.scope === 'company' || t.isCompanyTemplate));
    } catch (e) {
      setError('Failed to load company templates');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setForm(empty());
    setIsCreating(true);
    setIsEditing(false);
    setSelected(null);
  };

  const startEdit = (t: PromptTemplate) => {
    setSelected(t);
    setForm({
      title: t.title,
      prompt: t.prompt || '',
      description: t.description || '',
      category: t.category,
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const cancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (!selected) setForm(empty());
  };

  const save = async () => {
    try {
      setError(null);
      if (isCreating) {
        const created = await promptTemplateService.createTemplate({
          ...form,
          isCompanyTemplate: true,
        });
        setSelected(created);
      } else if (isEditing && selected) {
        const updated = await promptTemplateService.updateTemplate(selected.id, form);
        setSelected(updated);
      }
      // Flip out of edit mode immediately so the user sees the saved template
      // without waiting for the sidebar reload.
      setIsCreating(false);
      setIsEditing(false);
      void load();
    } catch (e: any) {
      setError(e.message || 'Failed to save template');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this company template? Users will no longer see it in their slash picker.')) {
      return;
    }
    try {
      setError(null);
      await promptTemplateService.deleteTemplate(id);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete template');
    }
  };

  const editing = isCreating || isEditing;

  return (
    <div className="bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-secondary dark:text-dark-text">Company Prompt Templates</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Templates you create here are available to every user in your company via the slash picker.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
        >
          + New Company Template
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Company Templates ({templates.length})
              </h3>
            </div>
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
            ) : templates.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No company templates yet
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[60vh] overflow-y-auto">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setSelected(t);
                      setIsEditing(false);
                      setIsCreating(false);
                    }}
                    className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      selected?.id === t.id && !editing
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                        : 'border-l-4 border-l-transparent'
                    }`}
                  >
                    <h4 className="font-semibold text-sm truncate">{t.title}</h4>
                    {t.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{t.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500">
                      <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                        Company
                      </span>
                      <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">{t.category}</span>
                      <span>{t.usageCount} uses</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <main className="lg:col-span-8">
          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
              className="space-y-5 border border-gray-200 dark:border-gray-700 rounded-lg p-5"
            >
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="What will users see in the slash picker?"
                  className="w-full px-4 py-3 text-lg font-semibold border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                  maxLength={100}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Short description of when to use this template"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={2}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Prompt
                  </label>
                  <span className="text-xs text-gray-400">{form.prompt.length.toLocaleString()} / 10,000</span>
                </div>
                <textarea
                  value={form.prompt}
                  onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                  placeholder="The prompt sent to the AI when a user picks this template..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm leading-relaxed"
                  rows={18}
                  required
                  maxLength={10000}
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 self-end">
                  <button
                    type="button"
                    onClick={cancel}
                    className="px-5 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {isCreating ? 'Publish to Company' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          ) : selected ? (
            <div className="space-y-4 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-2xl font-bold mb-1">{selected.title}</h2>
                  {selected.description && (
                    <p className="text-gray-600 dark:text-gray-400">{selected.description}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => startEdit(selected)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(selected.id)}
                    className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-xs font-medium">
                  Company-wide
                </span>
                <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-medium">
                  {selected.category}
                </span>
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-xs font-medium">
                  {selected.usageCount} uses
                </span>
              </div>
              <div>
                <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Prompt
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">{selected.prompt}</pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-12 text-center text-gray-500">
              <p className="font-medium mb-1">No template selected</p>
              <p className="text-sm">Pick a company template on the left, or create a new one.</p>
              <button
                onClick={startCreate}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                + New Company Template
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CompanyPromptTemplates;
